import { Show, createSignal, For, onCleanup } from 'solid-js';
import { wm } from '../lib/wmStore.js';
import { toast } from '../lib/toastStore.js';
import api from '../lib/apiClient.js';
import Tooltip from './MouseTooltip.jsx';

const VIEW_AS_TIP =
  'View as / Spy mode (admin)\n' +
  '\n' +
  'Opens a desktop view showing the target user\u2019s data and permissions, ' +
  'as if you were signed in as them. Use it to troubleshoot tickets, verify ' +
  'what a member actually sees, or audit widget access.\n' +
  '\n' +
  'Enter a Discord ID or @-tag, then press Enter or click \u201CView as\u201D.';

/**
 * Admin-only banner for impersonation + spy template ops.
 *
 * Visibility: only renders for admins. Pinned to the top of the desktop.
 * Switches into a red "impersonating" mode when ?as=<id> is active and
 * surfaces a one-click "Stop spying" affordance.
 *
 * Spy template controls let the admin save/load named layout templates.
 */
function applyAs(id, opts = {}) {
  const url = new URL(window.location.href);
  if (id) url.searchParams.set('as', id);
  else url.searchParams.delete('as');
  // Issue #18 — open in a new tab (admin keeps their own session) when
  // the caller asks for it (modifier-click, dedicated button).
  if (opts.newTab) window.open(url.toString(), '_blank', 'noopener');
  else window.location.href = url.toString();
}

export default function SpyBar() {
  const [input, setInput] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [suggestions, setSuggestions] = createSignal([]);
  const [showSug, setShowSug] = createSignal(false);
  const [cursor, setCursor] = createSignal(-1);

  // Named-template save dialog
  const [showSaveDialog, setShowSaveDialog] = createSignal(false);
  const [saveName, setSaveName] = createSignal('Spy Template');
  const [saveEmoji, setSaveEmoji] = createSignal('📋');

  // Named-template load panel
  const [showLoadPanel, setShowLoadPanel] = createSignal(false);
  const [templates, setTemplates] = createSignal([]);
  const [loadingTpls, setLoadingTpls] = createSignal(false);

  let debounce;
  let inputEl;

  const isAdmin       = () => !!wm.state.identity?.isAdmin;
  // Spy state is derived from the active dashboard, not the session identity.
  // This means the SpyBar switches to "is-spy" mode the moment the admin
  // focuses the spy desktop tab, and back to normal when they leave it.
  const spyDash       = () => wm.state.dashboards.find(
    d => d._spyTab && d.id === wm.state.activeDashboardId,
  );
  const impersonating = () => !!spyDash();
  const targetId      = () => spyDash()?._spyTarget?.id  || wm.state.identity?.targetId;
  const targetTag     = () => spyDash()?._spyTarget?.tag || wm.state.identity?.tag;

  // Close autocomplete dropdown when clicking outside
  function onDocClick(e) {
    if (!e.target.closest?.('.v2-spybar__autocomplete-wrap')) {
      setShowSug(false);
    }
    if (!e.target.closest?.('.v2-spybar__tpl-wrap')) {
      setShowSaveDialog(false);
      setShowLoadPanel(false);
    }
  }
  document.addEventListener('pointerdown', onDocClick, true);
  onCleanup(() => document.removeEventListener('pointerdown', onDocClick, true));

  // ── Autocomplete ──────────────────────────────────────────────────────
  async function fetchSuggestions(q) {
    if (!q || q.length < 1) { setSuggestions([]); setShowSug(false); return; }
    try {
      const res = await fetch(`/api/v2/admin/search-users?q=${encodeURIComponent(q)}`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const { results } = await res.json();
      setSuggestions(results || []);
      setShowSug((results || []).length > 0);
      setCursor(-1);
    } catch { /* ignore */ }
  }

  function onInput(e) {
    const v = e.currentTarget.value;
    setInput(v);
    clearTimeout(debounce);
    debounce = setTimeout(() => fetchSuggestions(v.trim()), 220);
  }

  function onKeyDown(e) {
    const sug = suggestions();
    if (!showSug() || !sug.length) {
      if (e.key === 'Enter') { startSpy(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor(c => Math.min(c + 1, sug.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor(c => Math.max(c - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const idx = cursor();
      if (idx >= 0 && sug[idx]) pickSuggestion(sug[idx]);
      else startSpy();
    } else if (e.key === 'Escape') {
      setShowSug(false);
      setCursor(-1);
    }
  }

  function pickSuggestion(s, opts = {}) {
    const id  = s.id  || s.tag || '';
    const tag = s.tag || s.id  || '';
    setInput('');
    setSuggestions([]);
    setShowSug(false);
    setCursor(-1);
    if (opts.newTab) {
      // Modifier-click: open in a new browser tab (preserves old behaviour).
      applyAs(id, { newTab: true });
    } else {
      // Primary path: spawn / switch to a spy desktop tab.
      wm.openSpyTab(id, tag);
    }
  }

  function startSpy(e) {
    e?.preventDefault?.();
    const v = input().trim();
    if (!v) { toast.warn('Enter a Discord ID or @-tag'); return; }
    // Ctrl/Cmd/Shift-click or middle-button: open in new browser tab (old path).
    const newTab = !!(e && (e.ctrlKey || e.metaKey || e.shiftKey));
    if (newTab) {
      applyAs(v, { newTab: true });
    } else {
      // Primary path: spawn a spy desktop tab and clear the input.
      wm.openSpyTab(v, v);
      setInput('');
      setSuggestions([]);
      setShowSug(false);
    }
  }

  function startSpyNewTab(e) {
    e?.preventDefault?.();
    const v = input().trim();
    if (!v) { toast.warn('Enter a Discord ID or @-tag'); return; }
    applyAs(v, { newTab: true });
  }

  // ── Named Templates ───────────────────────────────────────────────────
  function _collectWidgets() {
    const dash = wm.activeDashboard();
    if (!dash) return null;
    return (dash.widgets || []).map(w => ({
      widget_type: w.widget_type,
      x: w.x, y: w.y, w: w.w, h: w.h, z: w.z || 0,
      minimized: !!w.minimized, maximized: !!w.maximized,
      settings: w.settings || {},
    }));
  }

  function openSaveDialog() {
    setSaveName('Spy Template');
    setSaveEmoji('📋');
    setShowLoadPanel(false);
    setShowSaveDialog(true);
  }

  async function confirmSave(e) {
    e?.preventDefault?.();
    const widgets = _collectWidgets();
    if (!widgets) { toast.warn('No active dashboard'); return; }
    const name  = saveName().trim() || 'Spy Template';
    const emoji = saveEmoji().trim() || '📋';
    setShowSaveDialog(false);
    setBusy(true);
    try {
      await api.createNamedTemplate(name, emoji, widgets);
      toast.success(`${emoji} "${name}" saved (${widgets.length} widgets)`);
    } catch (err) {
      toast.error(`Save failed: ${err.message || err}`);
    } finally { setBusy(false); }
  }

  async function openLoadPanel() {
    setShowSaveDialog(false);
    setShowLoadPanel(true);
    setLoadingTpls(true);
    try {
      const { templates: tpls } = await api.listNamedTemplates();
      setTemplates(tpls || []);
    } catch (err) {
      toast.error(`Could not load templates: ${err.message || err}`);
      setShowLoadPanel(false);
    } finally { setLoadingTpls(false); }
  }

  async function applyTemplate(tpl) {
    setShowLoadPanel(false);
    setBusy(true);
    try {
      const { widgets } = await api.getNamedTemplate(tpl.id);
      if (!widgets?.length) { toast.info('Template is empty'); return; }
      if (typeof wm.applySpyTemplate === 'function') {
        await wm.applySpyTemplate(widgets);
        toast.success(`${tpl.emoji} "${tpl.name}" applied (${widgets.length} widgets)`);
      } else {
        toast.warn('applySpyTemplate not available');
      }
    } catch (err) {
      toast.error(`Load failed: ${err.message || err}`);
    } finally { setBusy(false); }
  }

  async function deleteTemplate(e, tpl) {
    e.stopPropagation();
    if (!confirm(`Delete "${tpl.emoji} ${tpl.name}"?`)) return;
    try {
      await api.deleteNamedTemplate(tpl.id);
      setTemplates(t => t.filter(x => x.id !== tpl.id));
      toast.success('Template deleted');
    } catch (err) {
      toast.error(`Delete failed: ${err.message || err}`);
    }
  }

  return (
    <Show when={isAdmin()}>
      <div class={`v2-spybar ${impersonating() ? 'is-spy' : ''}`}>
        <Tooltip text={VIEW_AS_TIP}>
          <span class="v2-spybar__icon" tabindex="0" aria-label="Admin View as / Spy mode help">
            {impersonating() ? '🕵' : '🛡'}
          </span>
        </Tooltip>
        <Show when={impersonating()}
          fallback={<span class="v2-spybar__label">Admin</span>}>
          <span class="v2-spybar__label">
            Spying as <b>{targetTag() || targetId()}</b>
          </span>
          <button class="v2-spybar__btn v2-spybar__btn--danger"
            onClick={() => wm.closeSpyTab()}>Stop spying</button>
        </Show>

        {/* ── View As autocomplete ── */}
        <div class="v2-spybar__autocomplete-wrap">
          <form class="v2-spybar__form" onSubmit={startSpy}>
            <input
              ref={inputEl}
              class="v2-spybar__input"
              type="text"
              placeholder="Discord ID or @tag…"
              value={input()}
              onInput={onInput}
              onKeyDown={onKeyDown}
              onFocus={() => suggestions().length && setShowSug(true)}
              autocomplete="off"
            />
            <Tooltip text={VIEW_AS_TIP + '\n\nTip: Ctrl/Cmd/Shift-click to open the impersonated desktop in a new tab.'}>
              <button class="v2-spybar__btn" type="submit" disabled={busy()}>View as →</button>
            </Tooltip>
          </form>
          <Show when={showSug() && suggestions().length > 0}>
            <div class="v2-spybar__dropdown" role="listbox">
              <For each={suggestions()}>{(s, i) => (
                <div
                  class={`v2-spybar__sug ${cursor() === i() ? 'is-active' : ''}`}
                  role="option"
                  title={
                    'Click to view as this user.\n' +
                    'Ctrl/Cmd or middle-click to open in a new tab.'
                  }
                  onPointerDown={(ev) => {
                    // Middle-click or modifier-click → new tab
                    const newTab = ev.button === 1 || ev.ctrlKey || ev.metaKey || ev.shiftKey;
                    if (ev.button === 2) return; // ignore right-click
                    ev.preventDefault();
                    pickSuggestion(s, { newTab });
                  }}
                >
                  <div class="v2-spybar__sug-row">
                    <span class="v2-spybar__sug-tag">{s.tag || '(no @-tag)'}</span>
                    <Show when={s.id}>
                      <span class="v2-spybar__sug-id">{s.id}</span>
                    </Show>
                    <Show when={s.source}>
                      <span class={`v2-spybar__sug-src v2-spybar__sug-src--${s.source}`}>
                        {s.source === 'linked'   ? 'linked'
                          : s.source === 'identity' ? 'synced'
                          : s.source === 'mm'       ? 'MM only'
                          : s.source}
                      </span>
                    </Show>
                  </div>
                  <Show when={(s.guilds && s.guilds.length) || s.saleCount}>
                    <div class="v2-spybar__sug-meta">
                      <Show when={s.guilds && s.guilds.length}>
                        <For each={s.guilds}>{(g) => (
                          <span class="v2-spybar__sug-guild">{g}</span>
                        )}</For>
                      </Show>
                      <Show when={s.saleCount}>
                        <span class="v2-spybar__sug-sales">{s.saleCount} sales · 90d</span>
                      </Show>
                    </div>
                  </Show>
                </div>
              )}</For>
            </div>
          </Show>
        </div>

        <div class="v2-spybar__sep" />

        {/* ── Named template buttons ── */}
        <div class="v2-spybar__tpl-wrap">
          <button class="v2-spybar__btn" disabled={busy()} onClick={openSaveDialog}
            title="Save current layout as a named template">💾 Save tpl</button>
          <button class="v2-spybar__btn" disabled={busy()} onClick={openLoadPanel}
            title="Load a saved named template">📂 Load tpl</button>

          {/* Save dialog */}
          <Show when={showSaveDialog()}>
            <form class="v2-spybar__tpl-dialog" onSubmit={confirmSave}>
              <input class="v2-spybar__tpl-emoji" type="text" maxlength="4"
                value={saveEmoji()} onInput={e => setSaveEmoji(e.currentTarget.value)}
                placeholder="📋" />
              <input class="v2-spybar__tpl-name" type="text" maxlength="60"
                value={saveName()} onInput={e => setSaveName(e.currentTarget.value)}
                placeholder="Template name…" autofocus />
              <button class="v2-spybar__btn" type="submit">Save</button>
              <button class="v2-spybar__btn" type="button"
                onClick={() => setShowSaveDialog(false)}>✕</button>
            </form>
          </Show>

          {/* Load panel */}
          <Show when={showLoadPanel()}>
            <div class="v2-spybar__tpl-panel">
              <div class="v2-spybar__tpl-panel-hdr">Templates</div>
              <Show when={loadingTpls()}>
                <div class="v2-spybar__tpl-loading">Loading…</div>
              </Show>
              <Show when={!loadingTpls() && templates().length === 0}>
                <div class="v2-spybar__tpl-empty">No templates saved yet.</div>
              </Show>
              <For each={templates()}>{(tpl) => (
                <div class="v2-spybar__tpl-row" onClick={() => applyTemplate(tpl)}>
                  <span class="v2-spybar__tpl-row-emoji">{tpl.emoji}</span>
                  <span class="v2-spybar__tpl-row-name">{tpl.name}</span>
                  <span class="v2-spybar__tpl-row-count">{tpl.widget_count}w</span>
                  <button class="v2-spybar__tpl-del" title="Delete"
                    onPointerDown={(e) => deleteTemplate(e, tpl)}>✕</button>
                </div>
              )}</For>
            </div>
          </Show>
        </div>
      </div>
      {/* ── Emulation banner (#134) ──
          Persistent dashboard banner during admin impersonation. Surfaces the
          target tag and reminds the admin that visible data + the add-widget
          catalog reflect the target's permissions, not their own. */}
      <Show when={impersonating()}>
        <div class="v2-spy-banner" role="status" aria-live="polite">
          <span class="v2-spy-banner__icon" aria-hidden="true">🕵</span>
          <span class="v2-spy-banner__text">
            Viewing as <b>{targetTag() ? `@${targetTag()}` : targetId()}</b>
            {' '}— visible data and menu options reflect their permissions.
          </span>
          <button
            class="v2-spy-banner__btn"
            type="button"
            onClick={() => wm.closeSpyTab()}
          >Stop spying</button>
        </div>
      </Show>
    </Show>
  );
}
